import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Edit2, X, Check, Search, UserCheck, UserX, Building2, Phone, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { useObras } from './useEstoqueConfig';

function FuncionarioForm({ inicial = {}, obras, onSalvar, onCancelar, salvando }) {
  const [dados, setDados] = useState({
    nome: inicial.nome || '',
    cpf: inicial.cpf || '',
    telefone: inicial.telefone || '',
    obra_atual: inicial.obra_atual || '',
    funcao: inicial.funcao || '',
    observacoes: inicial.observacoes || '',
  });

  const set = (k, v) => setDados(d => ({ ...d, [k]: v }));

  return (
    <div className="bg-white rounded-xl border-2 border-blue-200 p-5 space-y-4">
      <h3 className="font-semibold text-gray-800">{inicial.id ? 'Editar Funcionário' : 'Novo Funcionário'}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-gray-500 mb-1 block">Nome *</label>
          <input value={dados.nome} onChange={e => set('nome', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Nome completo" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">CPF</label>
          <input value={dados.cpf} onChange={e => set('cpf', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="000.000.000-00" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Telefone</label>
          <input value={dados.telefone} onChange={e => set('telefone', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="(41) 99999-9999" />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Empreendimento / Obra</label>
          <select value={dados.obra_atual} onChange={e => set('obra_atual', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="">Selecionar...</option>
            {obras.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Função / Cargo</label>
          <input value={dados.funcao} onChange={e => set('funcao', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Ex: Eletricista, Pedreiro..." />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-medium text-gray-500 mb-1 block">Observações</label>
          <textarea value={dados.observacoes} onChange={e => set('observacoes', e.target.value)} rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={() => onSalvar(dados)} disabled={salvando || !dados.nome.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
          <Check size={15} /> {salvando ? 'Salvando...' : 'Salvar'}
        </button>
        <button onClick={onCancelar}
          className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
          <X size={15} /> Cancelar
        </button>
      </div>
    </div>
  );
}

export default function EstoqueFuncionarios() {
  const qc = useQueryClient();
  const obras = useObras();
  const [busca, setBusca] = useState('');
  const [obraFiltro, setObraFiltro] = useState('');
  const [mostraInativos, setMostraInativos] = useState(false);
  const [novoAberto, setNovoAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['estoque-funcionarios', obraFiltro, mostraInativos],
    queryFn: () => api.get('/estoque/funcionarios', {
      params: { obra: obraFiltro || undefined, ativo: mostraInativos ? 'todos' : 'true' }
    }).then(r => r.data.data),
  });

  const funcionarios = (data || []).filter(f =>
    !busca || f.nome.toLowerCase().includes(busca.toLowerCase()) ||
    f.funcao?.toLowerCase().includes(busca.toLowerCase())
  );

  async function salvar(dados, id) {
    setSalvando(true); setErro('');
    try {
      if (id) {
        await api.put(`/estoque/funcionarios/${id}`, dados);
        setEditando(null);
      } else {
        await api.post('/estoque/funcionarios', dados);
        setNovoAberto(false);
      }
      qc.invalidateQueries({ queryKey: ['estoque-funcionarios'] });
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSalvando(false); }
  }

  async function desativar(id, nome) {
    if (!window.confirm(`Desativar ${nome}? O funcionário não aparecerá mais nas listagens.`)) return;
    await api.delete(`/estoque/funcionarios/${id}`);
    qc.invalidateQueries({ queryKey: ['estoque-funcionarios'] });
  }

  async function reativar(id) {
    await api.put(`/estoque/funcionarios/${id}`, { ativo: true });
    qc.invalidateQueries({ queryKey: ['estoque-funcionarios'] });
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Users size={24} className="text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Funcionários</h1>
          <p className="text-sm text-gray-500">Cadastro de funcionários que retiram ferramentas do estoque</p>
        </div>
      </div>

      {/* Filtros + Novo */}
      <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-48 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por nome ou função..."
            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Todos os empreendimentos</option>
          {obras.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={mostraInativos} onChange={e => setMostraInativos(e.target.checked)}
            className="rounded" />
          Mostrar inativos
        </label>
        <button onClick={() => { setNovoAberto(true); setEditando(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 ml-auto">
          <Plus size={16} /> Novo Funcionário
        </button>
      </div>

      {erro && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">
          <AlertCircle size={16} /> {erro}
        </div>
      )}

      {novoAberto && (
        <FuncionarioForm obras={obras} onSalvar={d => salvar(d, null)} onCancelar={() => setNovoAberto(false)} salvando={salvando} />
      )}

      {/* Lista */}
      <div className="space-y-3">
        {isLoading && <div className="text-center py-10 text-gray-400">Carregando...</div>}
        {!isLoading && funcionarios.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Users size={40} className="mx-auto mb-3 opacity-30" />
            <p>Nenhum funcionário cadastrado</p>
          </div>
        )}
        {funcionarios.map(f => (
          <div key={f.id}>
            {editando === f.id ? (
              <FuncionarioForm inicial={f} obras={obras}
                onSalvar={d => salvar(d, f.id)} onCancelar={() => setEditando(null)} salvando={salvando} />
            ) : (
              <div className={`bg-white rounded-xl shadow-sm border-l-4 p-4 flex items-center gap-4
                ${f.ativo ? 'border-green-400' : 'border-gray-300 opacity-60'}`}>
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0
                  ${f.ativo ? 'bg-blue-500' : 'bg-gray-400'}`}>
                  {f.nome.split(' ').map(n => n[0]).slice(0, 2).join('')}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">{f.nome}</span>
                    {!f.ativo && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inativo</span>}
                    {f.funcao && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{f.funcao}</span>}
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                    {f.obra_atual && (
                      <span className="flex items-center gap-1">
                        <Building2 size={11} /> {f.obra_atual}
                      </span>
                    )}
                    {f.telefone && (
                      <span className="flex items-center gap-1">
                        <Phone size={11} /> {f.telefone}
                      </span>
                    )}
                    {f.cpf && <span>CPF: {f.cpf}</span>}
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setEditando(f.id)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 size={15} />
                  </button>
                  {f.ativo ? (
                    <button onClick={() => desativar(f.id, f.nome)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Desativar">
                      <UserX size={15} />
                    </button>
                  ) : (
                    <button onClick={() => reativar(f.id)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Reativar">
                      <UserCheck size={15} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
